import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Calendar } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SelectDropdown from "../../../components/elements/forms/Select";
import SubmitButton from "../../../components/elements/forms/SubmitButton";
import Modal from "../../../components/Modal";
import ModalNew from "../../../components/Modal/ModalNew";
import CreateCandidateModal from "../misc/CreateCandidateModal";
import FileInput from "../../../components/elements/forms/FileInput";
import DownloadButton from "../misc/DownloadButton";
import CheckboxGroup from "../../../components/elements/forms/CheckboxGroup";
import { DropdownSelect } from "../../../components/elements/dropdown/DropdownSelect";
import { Country, State } from "country-state-city";
import {
  useBulkCandidates,
  useCandidate,
  useCandidateOptions,
} from "../misc/useCandidate";
import axiosInstance from "../../../utility/axiosInstance";
import { useAppDispatch } from "../../../store/hooks";
import { candidates as createCandidateApi } from "../../../services/api/candidate";
import { Skeleton, Switch } from "antd";
import img from "../../../constant";
import { IJobFetch } from "../../../store/types/job";
import { toLocalISOString } from "../../../utility/candidateHelper";
import CandidateSelectionTable from "../misc/CandidateSelectionTable";
import { candidateExternal } from "../misc/candidateExternal";

type ApiResponse = {
  message: string;
  data: IJobFetch[];
};

type CandidateType = {
  id: number;
  candidateId: string;
  interviewId: string;
  candidateIds: string[];
  job: string;
  date: string;
  interviewStatus: string;
  invitationStatus: string;
  actions: any;
  expirationDate: string;
};

const CandidateCreate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [jobs, setJobs] = useState<IJobFetch[]>([]);
  const [loading2, setLoading] = useState(true);
  const [error2, setError] = useState<Error | null>(null);
  const [multiValue, setMultiValue] = useState<string[]>([]);
  const [externalCandidateIds, setExternalCandidateIds] = useState<string[]>(
    [],
  );
  const [open, setOpen] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [formErrors, setFormErrors] = useState({
    interviewDate: "",
    expirationDate: "",
  });

  const isEditMode = location.pathname.includes("/edit-candidate");
  const candidateData = location.state?.candidateData as
    | CandidateType
    | undefined;

  // Hooks
  const { data, error, onChangeHandler, onChangeHandle, onSubmit, setData } =
    useCandidate(isEditMode, candidateData?.candidateId);

  const {
    data: bulkData,
    err: bulkErr,
    onChangeHandler: bulkOnChangeHandler,
    onChangeHandle: bulkOnChangeHandle,
    onSubmit: bulkOnSubmit,
  } = useBulkCandidates();

  const {
    candidateOptions,
    loading,
    refetch: refetchCandidates,
  } = useCandidateOptions();

  // Handle successful candidate creation
  const handleCreateSuccess = () => {
    // Refetch the candidates dropdown options
    refetchCandidates();
    // Close the modal
    setOpen(false);
  };

  // Fetch jobs
  const getJobs = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get<ApiResponse>("/job");
      setJobs(response.data.data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("An error occurred"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getJobs();
  }, []);

  // Date parsing helper (preserve local time)
  const parseDateToISO = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return !isNaN(date.getTime()) ? toLocalISOString(date) : "";
  };

  // Initialize edit mode data
  useEffect(() => {
    if (isEditMode && candidateData && jobs.length > 0 && !hasInitialized) {
      const selectedJob = jobs.find(
        (job) => job.currentTemplate.title === candidateData.job,
      );
      const interviewDate = parseDateToISO(candidateData.date);
      const expirationDate = parseDateToISO(candidateData.expirationDate);

      setData({
        candidateIds: candidateData.candidateIds || [""],
        jobId: selectedJob?.id || "",
        interviewDate,
        expirationDate,
      });
      setHasInitialized(true);
    }
  }, [isEditMode, candidateData, jobs, setData, hasInitialized]);

  // Merge selected external candidates with dropdown candidates
  useEffect(() => {
    if (
      data.hiringSource?.includes("25th Master Record") &&
      externalCandidateIds.length > 0
    ) {
      // Combine external candidates with manually selected ones (ES5 compatible)
      const allIds = [...multiValue, ...externalCandidateIds];
      const combined = allIds.filter(function (id, index) {
        return allIds.indexOf(id) === index;
      });
      data.candidateIds = combined;
    } else {
      data.candidateIds = multiValue;
    }
  }, [multiValue, externalCandidateIds, data.hiringSource]);

  // Helpers
  const handleModalClose = () => setOpen(false);

  const getSelectedDate = (value: string | undefined) => {
    if (!value) return null;
    try {
      return new Date(value);
    } catch {
      return null;
    }
  };

  // Submit
  const dispatch = useAppDispatch();
  const handleSubmit = async () => {
    const currentData = isChecked ? bulkData : data;
    const newErrors = {
      interviewDate: currentData.interviewDate ? "" : "Interview date required",
      expirationDate: currentData.expirationDate
        ? ""
        : "Expiration date required",
    };
    setFormErrors(newErrors);

    if (newErrors.interviewDate || newErrors.expirationDate) return;

    setLoadingSubmit(true);
    try {
      if (isChecked) {
        await bulkOnSubmit();
      } else {
        // Handle external candidates for single creation
        let finalCandidateIds = [...(data.candidateIds || [])];

        const externalIdsToProcess = finalCandidateIds.filter((id) =>
          candidateExternal.data.some((ext) => ext.id === id),
        );

        if (externalIdsToProcess.length > 0) {
          const newRealIds: string[] = [];

          for (const extId of externalIdsToProcess) {
            const extCandidate = candidateExternal.data.find(
              (c) => c.id === extId,
            );
            if (extCandidate) {
              const formData = new FormData();
              formData.append(
                "name",
                `${extCandidate.firstName} ${extCandidate.lastName}`,
              );
              formData.append("email", extCandidate.email);
              formData.append(
                "phoneNumber",
                extCandidate.phone || "0000000000",
              );
              formData.append(
                "jobTitle",
                extCandidate.professionalTitle || "Candidate",
              );
              formData.append("address", extCandidate.city || "N/A");

              try {
                const res = await createCandidateApi(dispatch, formData);
                console.log("Created external candidate:", res);
                if (res?.data?.id) {
                  newRealIds.push(res.data.id);
                } else if (res?.data?.data?.id) {
                  newRealIds.push(res.data.data.id);
                }
              } catch (err) {
                console.error("Failed to create external candidate", err);
                // Continue? Or fail? Let's continue with others but this one might fail interview creation
              }
            }
          }

          // Replace external IDs with new real IDs
          finalCandidateIds = finalCandidateIds.filter(
            (id) => !externalIdsToProcess.includes(id),
          );
          finalCandidateIds.push(...newRealIds);
        }

        await onSubmit({ candidateIds: finalCandidateIds });
      }
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <div className="w-full py-5">
      <div className="max-w-[1096px] mx-auto px-3">
        {/* Tabs */}
        <div className="flex flex-wrap border-b border-border text-20">
          <div
            onClick={() => navigate(`/create-job`)}
            className="pb-1 cursor-pointer md:mr-8 mr-5"
          >
            <p className="font-[400]">Add Job</p>
          </div>
          <div
            onClick={() => navigate(`/create-interview`)}
            className={`pb-1 cursor-pointer md:mr-8 mr-5 ${
              !isEditMode ? "border-b-[3px] border-secondary" : ""
            }`}
          >
            <p className="font-[400]">
              {isEditMode ? "Edit Interview" : "Create Interview"}
            </p>
          </div>
        </div>

        {/* Main Card */}
        <div className="rounded-10 bg-white px-4 py-5 mt-5 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Job Dropdown */}
            <SelectDropdown
              items={jobs?.map((job) => ({
                id: job.id,
                name: job?.currentTemplate.title,
              }))}
              name="jobId"
              label="Job Title"
              status={isChecked ? bulkErr.jobId : error.jobId}
              value={isChecked ? bulkData.jobId || "" : data.jobId || ""}
              onChange={isChecked ? bulkOnChangeHandle : onChangeHandle}
              required
            />
            <div className="col-span-1">
              <SelectDropdown
                items={[
                  { id: "entryLevel", name: "Entry Level" },
                  { id: "juniorLevel", name: "Junior Level" },
                  { id: "midLevel", name: "Mid Level" },
                  { id: "seniorLevel", name: "Senior Level" },
                  { id: "cSuite", name: "C-Suite" },
                ]}
                name="roleClassification"
                label="Role Classification"
                value={data.roleClassification || ""}
                onChange={onChangeHandle}
              />
            </div>
          </div>
          <p className="text-15 font-semibold mb-2">Candidate Hiring Source</p>
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-3 items-center">
            <div className="col-span-5">
              <CheckboxGroup
                options={[
                  { label: "25th Master Record", value: "25th Master Record" },
                  // { label: "LinkedIn", value: "LinkedIn" },
                  // { label: "Indeed", value: "Indeed" },
                  // { label: "Jobberman", value: "Jobberman" },
                  { label: "Custom Record", value: "Custom Record" },
                ]}
                selectedValues={data.hiringSource || []}
                onChange={(values) => setData({ hiringSource: values })}
              />
            </div>
          </div>

          {/* Candidate Master Record  */}
          {data.hiringSource?.includes("25th Master Record") && (
            <div className="mb-6">
              <CandidateSelectionTable
                jobTitle={
                  jobs?.find((j) => j.id === data.jobId)?.currentTemplate?.title
                }
                onSelectionChange={setExternalCandidateIds}
                externalData={candidateExternal.data}
              />
            </div>
          )}

          {/* Candidate Upload / Select */}
          <div className="transition-all duration-300">
            {isChecked ? (
              <div>
                <p className="text-15 font-semibold mb-2">
                  Upload Bulk Candidates
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <FileInput
                    label="Upload CSV file"
                    name="csvFile"
                    status={bulkErr.csvFile}
                    onChange={bulkOnChangeHandler}
                  />
                  <DownloadButton size="md" />
                </div>
              </div>
            ) : (
              <div className="mb-3">
                <p className="text-15 font-semibold mb-2">Select Candidate</p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  {loading ? (
                    <Skeleton active paragraph={{ rows: 1 }} />
                  ) : (
                    <DropdownSelect
                      options={candidateOptions}
                      value={multiValue || data.candidateIds || [""]}
                      onChange={setMultiValue}
                      placeholder="Select..."
                      searchable
                      multiSelect
                    />
                  )}
                  <button
                    className="h-[47px] max-w-[160px] w-full mb-2 flex items-center justify-center bg-secondary text-white rounded-8 cursor-pointer"
                    onClick={() => setOpen(true)}
                  >
                    Add New Candidate
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Toggle */}
          <div className="flex items-center mb-4">
            <Switch checked={isChecked} onChange={setIsChecked} />
            <span className="text-lg font-medium text-gray-700 ml-3">
              {isChecked ? "Create Candidate" : "Upload Bulk Candidate"}
            </span>
          </div>

          {/* Date Pickers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            {/* Interview Date */}
            <div className="flex flex-col">
              <label className="font-semibold text-15 text-[#1E1E1E] mb-1">
                Schedule Interview Date <span className="text-[red]">*</span>
              </label>
              <div
                className={`relative flex items-center w-full rounded-lg border ${
                  formErrors.interviewDate ? "border-red-500" : "border-primary"
                }`}
              >
                <DatePicker
                  selected={getSelectedDate(
                    isChecked ? bulkData.interviewDate : data.interviewDate,
                  )}
                  onChange={(date: Date | null) => {
                    const handler = isChecked
                      ? bulkOnChangeHandler
                      : onChangeHandler;
                    handler({
                      target: {
                        name: "interviewDate",
                        value: date ? toLocalISOString(date) : "",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  placeholderText="dd/mm/yyyy, --:--"
                  className="w-full py-2 px-3 pr-10 rounded-md focus:outline-none text-gray-700 text-sm sm:text-base"
                  wrapperClassName="w-full"
                  popperClassName="z-50"
                  popperPlacement="bottom-start"
                  portalId="root-portal"
                />
                <Calendar className="absolute right-3 text-gray-400 pointer-events-none w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              {formErrors.interviewDate && (
                <span className="text-red-500 text-xs mt-1">
                  {formErrors.interviewDate}
                </span>
              )}
            </div>

            {/* Expiration Date */}
            <div className="flex flex-col">
              <label className="font-semibold text-15 text-[#1E1E1E] mb-1">
                Interview Expiration Date <span className="text-[red]">*</span>
              </label>
              <div
                className={`relative flex items-center w-full rounded-lg border ${
                  formErrors.expirationDate
                    ? "border-red-500"
                    : "border-primary"
                }`}
              >
                <DatePicker
                  selected={getSelectedDate(
                    isChecked ? bulkData.expirationDate : data.expirationDate,
                  )}
                  onChange={(date: Date | null) => {
                    const handler = isChecked
                      ? bulkOnChangeHandler
                      : onChangeHandler;
                    handler({
                      target: {
                        name: "expirationDate",
                        value: date ? toLocalISOString(date) : "",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  placeholderText="dd/mm/yyyy, --:--"
                  className="w-full py-2 px-3 pr-10 rounded-md focus:outline-none text-gray-700 text-sm sm:text-base"
                  wrapperClassName="w-full"
                  popperClassName="z-50"
                  popperPlacement="bottom-start"
                  portalId="root-portal"
                />
                <Calendar className="absolute right-3 text-gray-400 pointer-events-none w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              {formErrors.expirationDate && (
                <span className="text-red-500 text-xs mt-1">
                  {formErrors.expirationDate}
                </span>
              )}
            </div>
          </div>

          {/* Submit */}
          <div className="mt-6">
            <SubmitButton
              title={isEditMode ? "Update Interview" : "Create Interview"}
              onSubmitHandler={handleSubmit}
              loading={loadingSubmit}
              className="font-[400] text-white md:hover:bg-[white] md:hover:text-secondary hover:border-primary"
            />
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className="mt-2 text-center">
          <p className="text-xl md:text-5xl text-black-500">
            {isEditMode
              ? "Candidate successfully updated"
              : "Job successfully created"}
          </p>
          <div className="flex justify-center my-6">
            <img src={img.thanks} alt="thank you" />
          </div>
          <div className="mt-8">
            <Link
              to={"/"}
              onClick={() => setIsModalOpen(false)}
              className="rounded-xl bg-black px-8 py-4 text-white hover:bg-blue-600 cursor-pointer"
            >
              Back to home
            </Link>
          </div>
        </div>
      </Modal>

      {/* Add Candidate Modal */}
      <ModalNew isOpen={open} onClose={handleModalClose} title="Add Candidate">
        <CreateCandidateModal onSuccess={handleCreateSuccess} />
      </ModalNew>
    </div>
  );
};

export default CandidateCreate;
